import { useEffect, useRef, useState } from "react";

export const useIsItemOverflowing = (
  element: HTMLElement | null | undefined
): boolean => {
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  // Track the last container width when we made a decision
  // This prevents flip-flopping when text content changes
  const lastContainerWidthRef = useRef<number | null>(null);
  const isOverflowingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!element) {
      setIsOverflowing(false);
      lastContainerWidthRef.current = null;
      isOverflowingRef.current = false;
      return;
    }

    let rafId: number | null = null;

    const measure = () => {
      const currentContainerWidth = element.clientWidth;
      const currentScrollWidth = element.scrollWidth;
      const currentlyOverflowing = currentScrollWidth > currentContainerWidth;

      // If we haven't measured before, just set the initial state
      if (lastContainerWidthRef.current === null) {
        lastContainerWidthRef.current = currentContainerWidth;
        isOverflowingRef.current = currentlyOverflowing;
        setIsOverflowing(currentlyOverflowing);
        return;
      }

      // Only re-evaluate if the container width has changed significantly
      // (more than 1px to account for sub-pixel rendering)
      const containerWidthChanged =
        Math.abs(currentContainerWidth - lastContainerWidthRef.current) > 1;

      if (containerWidthChanged) {
        // Container size changed (e.g., window resize), re-evaluate
        lastContainerWidthRef.current = currentContainerWidth;
        isOverflowingRef.current = currentlyOverflowing;
        setIsOverflowing(currentlyOverflowing);
      }
      // If container width hasn't changed, keep the current decision
      // This prevents the flip-flop when switching between name/nameBreakable
    };

    // measure now and on next frame (to catch late layout/font changes)
    measure();
    rafId = window.requestAnimationFrame(measure);

    const onResize = () => {
      // Reset the last width on window resize to force re-evaluation
      lastContainerWidthRef.current = null;
      measure();
    };
    window.addEventListener("resize", onResize);

    const ro = new ResizeObserver(() => {
      // ResizeObserver fires when element size changes
      // We need to be careful here - only re-measure if it's likely
      // a container resize, not a content change
      measure();
    });
    ro.observe(element);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [element]);

  return isOverflowing;
};

export const useIsItemOverflowingPct = (
  element: HTMLElement | null | undefined,
  pct?: number
): boolean => {
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  // Track the last container width when we made a decision
  // This prevents flip-flopping when text content changes
  const lastContainerWidthRef = useRef<number | null>(null);

  useEffect(() => {
    if (!element) {
      setIsOverflowing(false);
      lastContainerWidthRef.current = null;
      return;
    }

    let rafId: number | null = null;

    const measure = () => {
      const currentContainerWidth = element.clientWidth;
      const currentScrollWidth = element.scrollWidth;

      let currentlyOverflowing: boolean;
      if (currentScrollWidth > currentContainerWidth) {
        currentlyOverflowing = true;
      } else {
        const overFlowInPct = currentContainerWidth / currentScrollWidth;
        currentlyOverflowing = overFlowInPct > (pct ?? 1);
      }

      // If we haven't measured before, just set the initial state
      if (lastContainerWidthRef.current === null) {
        lastContainerWidthRef.current = currentContainerWidth;
        setIsOverflowing(currentlyOverflowing);
        return;
      }

      // Only re-evaluate if the container width has changed significantly
      // (more than 1px to account for sub-pixel rendering)
      const containerWidthChanged =
        Math.abs(currentContainerWidth - lastContainerWidthRef.current) > 1;

      if (containerWidthChanged) {
        // Container size changed (e.g., window resize), re-evaluate
        lastContainerWidthRef.current = currentContainerWidth;
        setIsOverflowing(currentlyOverflowing);
      }
      // If container width hasn't changed, keep the current decision
      // This prevents the flip-flop when switching between name/nameBreakable
    };

    // measure now and on next frame (to catch late layout/font changes)
    measure();
    rafId = window.requestAnimationFrame(measure);

    const onResize = () => {
      // Reset the last width on window resize to force re-evaluation
      lastContainerWidthRef.current = null;
      measure();
    };
    window.addEventListener("resize", onResize);

    const ro = new ResizeObserver(() => {
      // ResizeObserver fires when element size changes
      // We need to be careful here - only re-measure if it's likely
      // a container resize, not a content change
      measure();
    });
    ro.observe(element);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [element, pct]);

  return isOverflowing;
};
